import ThumbImg from "../media/ThumbImg";
import { useEffect, useState } from "react";

// Rotating image-gallery visual for the landing/favorites dashboard. Extracted
// verbatim from ChatLayout (Phase 7 safe win) — self-contained, React-only, props
// typed inline (ground rule 5: no back-imports from ChatLayout).
function FavGalleryVisual({
  frames,
  fit,
  hover,
  rotate,
  seconds,
  pause,
  transition = 'fade',
  showPeopleHandle,
  onOpenChannels,
  onOpenPeople,
}: {
  frames: { url: string; name?: string }[];
  fit: 'contain-blur'|'cover';
  hover: 'subtle'|'none';
  rotate: boolean;
  seconds: number;
  pause: boolean;
  transition?: 'fade'|'snap';
  showPeopleHandle?: boolean;
  onOpenChannels?: () => void;
  onOpenPeople?: () => void;
}) {
  const [idx, setIdx] = useState(0);
  const [prevIdx, setPrevIdx] = useState<number|null>(null);
  const [isHover, setIsHover] = useState(false);
  const [fadePhase, setFadePhase] = useState<'idle'|'in'>('idle');
  useEffect(() => {
    if (!rotate || !frames || frames.length <= 1) return;
    if (pause && isHover) return;
    const ms = Math.max(3000, Math.min(60000, (seconds||8)*1000));
    const t = setInterval(() => {
      setIdx((i) => {
        setPrevIdx(i);
        if (transition === 'fade') setFadePhase('in');
        return (i + 1) % frames.length;
      });
    }, ms);
    return () => clearInterval(t);
  }, [rotate, frames, seconds, pause, isHover, transition]);
  useEffect(() => {
    if (fadePhase === 'in') {
      const t = setTimeout(() => { setFadePhase('idle'); setPrevIdx(null); }, 500);
      return () => clearTimeout(t);
    }
  }, [fadePhase]);
  if (!frames || frames.length === 0) {
    return <div className="absolute inset-0 flex items-center justify-center text-fg-3">No images yet</div>;
  }
  const cur = frames[Math.max(0, Math.min(idx, frames.length-1))];
  const prev = prevIdx!=null ? frames[prevIdx] : null;
  const hoverCls = hover==='subtle' ? '' : '';
  return (
    <div className="absolute inset-0" onMouseEnter={()=>setIsHover(true)} onMouseLeave={()=>setIsHover(false)}>
      {/* Solid base fills any transparent pixels from PNGs */}
      <div className="absolute inset-0 bg-neutral-900" />
      {fit==='contain-blur' ? (
        <>
          {/* Blurred backdrop to avoid letterboxing */}
          <ThumbImg w={480} src={(prev && transition==='fade') ? prev.url : cur.url} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-md opacity-60 bg-neutral-900" />
          {/* Foreground image(s) */}
          {transition==='fade' && prev ? (
            <>
              <ThumbImg w={480} src={prev.url} alt={prev.name||'image'} className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${fadePhase==='in' ? 'opacity-0' : 'opacity-100'}`} />
              <ThumbImg w={480} src={cur.url} alt={cur.name||'image'} className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${fadePhase==='in' ? 'opacity-100' : 'opacity-100'} ${hoverCls}`} />
            </>
          ) : (
            <ThumbImg w={480} src={cur.url} alt={cur.name||'image'} className={`absolute inset-0 w-full h-full object-contain ${hoverCls}`} />
          )}
        </>
      ) : (
        transition==='fade' && prev ? (
          <>
            <ThumbImg w={480} src={prev.url} alt={prev.name||'image'} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${fadePhase==='in' ? 'opacity-0' : 'opacity-100'} bg-neutral-900`} />
            <ThumbImg w={480} src={cur.url} alt={cur.name||'image'} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${fadePhase==='in' ? 'opacity-100' : 'opacity-100'} ${hoverCls} bg-neutral-900`} />
          </>
        ) : (
          <ThumbImg w={480} src={cur.url} alt={cur.name||'image'} className={`absolute inset-0 w-full h-full object-cover ${hoverCls} bg-neutral-900`} />
        )
      )}
      <div className="sm:hidden fixed inset-y-0 left-0 z-30 flex flex-col justify-center pl-1 pointer-events-none">
        <button
          className="pointer-events-auto h-20 w-8 rounded-r-full bg-black/60 border border-white/10 text-fg-2 hover:text-white flex items-center justify-center"
          aria-label="Open channels"
          title="Channels"
          onClick={() => { onOpenChannels?.(); }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
      {showPeopleHandle && (
        <div className="sm:hidden fixed inset-y-0 right-0 z-30 flex flex-col justify-center pr-1 pointer-events-none">
          <button
            className="pointer-events-auto h-20 w-8 rounded-l-full bg-black/60 border border-white/10 text-fg-2 hover:text-white flex items-center justify-center"
            aria-label="Open people"
            title="People"
            onClick={() => { onOpenPeople?.(); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
        </div>
      )}


    </div>
  );
}

export default FavGalleryVisual;
